import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { getDashboardStats, getAIAnalysis, seedDemoData, seedRegulatoryClauses, getBulletins } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  AlertTriangle, Shield, FileWarning, ClipboardCheck, 
  TrendingUp, TrendingDown, Minus, Activity, Brain,
  Plus, RefreshCw, Sparkles, Megaphone, Bell, ChevronRight,
  Users, BookOpen, Calendar, Settings, BarChart3, Target,
  Plane, GraduationCap, FileCheck, Clock, CheckCircle2,
  XCircle, AlertCircle, Zap, Eye, EyeOff, Award, FileDown
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import { Play } from 'lucide-react';
import PolishBatchPanel from '@/components/PolishBatchPanel';

const RISK_COLORS = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#eab308',
  low: '#10b981',
  acceptable: '#10b981'
};

const SEVERITY_COLORS = ['#dc2626', '#ea580c', '#eab308', '#22c55e', '#64748b'];

// Role-based widget configurations
const ROLE_WIDGETS = {
  admin: ['stats', 'incidents_trend', 'risks_overview', 'capas_overview', 'audits_overview', 'ai_analysis', 'bulletins', 'users', 'compliance'],
  safety_manager: ['stats', 'incidents_trend', 'risks_overview', 'capas_overview', 'audits_overview', 'ai_analysis', 'bulletins', 'training'],
  safety_officer: ['stats', 'incidents_trend', 'risks_overview', 'capas_overview', 'bulletins', 'my_tasks'],
  auditor: ['stats', 'audits_overview', 'capas_overview', 'compliance', 'bulletins'],
  investigator: ['stats', 'incidents_trend', 'risks_overview', 'my_investigations', 'bulletins'],
  trainer: ['stats', 'training', 'bulletins', 'sessions'],
  pilot: ['stats', 'incidents_trend', 'bulletins', 'my_reports', 'training_due'],
  crew: ['stats', 'bulletins', 'my_reports', 'training_due'],
  inspector: ['stats', 'compliance', 'audits_overview', 'incidents_trend', 'risks_overview']
};

// Widget visibility settings
const DEFAULT_WIDGET_VISIBILITY = {
  stats: true,
  incidents_trend: true,
  risks_overview: true,
  capas_overview: true,
  audits_overview: true,
  ai_analysis: true,
  bulletins: true,
  users: true,
  compliance: true,
  training: true,
  my_tasks: true,
  my_investigations: true,
  sessions: true,
  my_reports: true,
  training_due: true
};

export default function DashboardPage({ onNavigate }) {
  const { t } = useTranslation();
  const { api, user, token } = useAuth();
  const { isRTL, language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [recentBulletins, setRecentBulletins] = useState([]);
  const [pendingSurveyCount, setPendingSurveyCount] = useState(0);
  const [readiness, setReadiness] = useState(null);
  const [actionPlan, setActionPlan] = useState(null);
  const [actionPlanLoading, setActionPlanLoading] = useState(false);
  const [actionPlanOpen, setActionPlanOpen] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [downloadingAdminGuide, setDownloadingAdminGuide] = useState(false);
  const [widgetVisibility, setWidgetVisibility] = useState(() => {
    const saved = localStorage.getItem('dashboardWidgets');
    return saved ? JSON.parse(saved) : DEFAULT_WIDGET_VISIBILITY;
  });

  const handleDownloadAdminGuide = async () => {
    setDownloadingAdminGuide(true);
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL || ''}/AeroGuard_Admin_Setup_Guide.pdf`;
      const res = await fetch(url, { cache: 'no-store', credentials: 'omit' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const objUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = 'AeroGuard_Admin_Setup_Guide.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(objUrl), 1000);
      toast.success('Admin Setup Guide downloaded');
    } catch (err) {
      console.error('Admin guide download failed:', err);
      toast.error('Failed to download the Admin Setup Guide. Please try again.');
    } finally {
      setDownloadingAdminGuide(false);
    }
  };

  // Get available widgets for current user role
  const availableWidgets = ROLE_WIDGETS[user?.role] || ROLE_WIDGETS.crew;

  useEffect(() => {
    fetchDashboardData();
    fetchRecentBulletins();
    fetchPendingSurveyCount();
    fetchReadiness();
  }, []);

  useEffect(() => {
    localStorage.setItem('dashboardWidgets', JSON.stringify(widgetVisibility));
  }, [widgetVisibility]);

  const fetchDashboardData = async () => {
    try {
      const response = await getDashboardStats(api);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentBulletins = async () => {
    try {
      const response = await getBulletins(api, { active_only: true, limit: 3 });
      setRecentBulletins(response.data || []);
    } catch (error) {
      console.error('Failed to fetch bulletins:', error);
    }
  };

  const fetchPendingSurveyCount = async () => {
    try {
      const res = await api.get('/gaca-p1/training-effectiveness/pending');
      setPendingSurveyCount((res.data?.items || []).length);
    } catch (error) {
      // Silent — endpoint optional / new
    }
  };

  const fetchReadiness = async () => {
    try {
      const res = await api.get('/gaca-p1/readiness/summary');
      setReadiness(res.data);
    } catch (error) {
      // Silent — widget hides if endpoint unavailable
    }
  };

  const [actionPlanError, setActionPlanError] = useState(null);
  const openActionPlan = async () => {
    setActionPlanOpen(true);
    setActionPlanLoading(true);
    setActionPlanError(null);
    try {
      const res = await api.get('/gaca-p1/readiness/action-plan');
      setActionPlan(res.data);
    } catch (error) {
      setActionPlan(null);
      setActionPlanError(error?.response?.data?.detail || error?.message || 'Failed to load action plan');
    } finally {
      setActionPlanLoading(false);
    }
  };

  const convertActionToCapa = async (actionId) => {
    try {
      const res = await api.post(`/gaca-p1/readiness/action-plan/${actionId}/convert-to-capa`);
      const data = res.data || {};
      if (data.already_linked) {
        toast.info(`Already tracked as ${data.capa_number}`);
      } else {
        toast.success(`Created ${data.capa_number} — due ${(data.due_date || '').slice(0, 10)}`);
      }
      // Re-fetch so the action now shows linked_capa_id
      await openActionPlan();
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.message || 'Failed to create CAPA';
      toast.error(msg);
    }
  };

  const handleSeedData = async () => {
    try {
      await seedRegulatoryClauses(api);
      await seedDemoData(api);
      fetchDashboardData();
    } catch (error) {
      console.error('Failed to seed data:', error);
    }
  };

  const handleGetAIAnalysis = async () => {
    setAiLoading(true);
    try {
      const response = await getAIAnalysis(api);
      setAiAnalysis(response.data);
    } catch (error) {
      console.error('Failed to get AI analysis:', error);
    } finally {
      setAiLoading(false);
    }
  };

  const toggleWidget = (widgetId) => {
    setWidgetVisibility(prev => ({
      ...prev,
      [widgetId]: !prev[widgetId]
    }));
  };

  const isWidgetVisible = (widgetId) => {
    return availableWidgets.includes(widgetId) && widgetVisibility[widgetId];
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving': return <TrendingDown className="w-4 h-4 text-emerald-400" />;
      case 'declining': return <TrendingUp className="w-4 h-4 text-red-400" />;
      default: return <Minus className="w-4 h-4 text-slate-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const incidentTrendData = stats?.incidents?.monthly_trend || [];
  
  const severityData = Object.entries(stats?.incidents?.by_severity || {}).map(([name, value]) => ({
    name: t(`incidents.severities.${name}`) || name,
    value,
    fill: RISK_COLORS[name] || '#64748b'
  }));

  // Widget definitions
  const WIDGET_CONFIG = {
    stats: { title: 'Key Metrics', icon: BarChart3 },
    incidents_trend: { title: 'Incident Trends', icon: TrendingUp },
    risks_overview: { title: 'Risk Overview', icon: AlertTriangle },
    capas_overview: { title: 'CAPA Status', icon: ClipboardCheck },
    audits_overview: { title: 'Audit Schedule', icon: Shield },
    ai_analysis: { title: 'AI Analysis', icon: Brain },
    bulletins: { title: 'Safety Alerts', icon: Megaphone },
    users: { title: 'User Activity', icon: Users },
    compliance: { title: 'Compliance', icon: FileCheck },
    training: { title: 'Training', icon: GraduationCap },
    my_tasks: { title: 'My Tasks', icon: Target },
    my_investigations: { title: 'My Investigations', icon: Eye },
    sessions: { title: 'Training Sessions', icon: Calendar },
    my_reports: { title: 'My Reports', icon: FileWarning },
    training_due: { title: 'Training Due', icon: Clock }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Polish Batch Retro-Fix — visible to Safety/Quality Manager + Admin only */}
      <PolishBatchPanel token={token} userRole={user?.role} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white font-barlow tracking-tight" data-testid="dashboard-title">
            {t('dashboard.title')}
          </h1>
          <div className="flex items-center gap-2 text-slate-400 mt-1">
            <span>{t('dashboard.welcome')}, {user?.name}</span>
            <Badge variant="outline" className="text-xs capitalize border-slate-600 text-slate-400">
              {user?.role?.replace('_', ' ')}
            </Badge>
            {pendingSurveyCount > 0 && (
              <button
                type="button"
                onClick={() => onNavigate('training-compliance')}
                className="ml-1 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/15 border border-amber-500/40 text-amber-300 hover:bg-amber-500/25 hover:border-amber-500/60 transition-colors"
                data-testid="dashboard-pending-survey-pill"
                aria-label={`${pendingSurveyCount} pending training effectiveness surveys`}
              >
                <Sparkles className="w-3 h-3" />
                {pendingSurveyCount} pending {pendingSurveyCount === 1 ? 'survey' : 'surveys'}
                <ChevronRight className="w-3 h-3 -mr-0.5" />
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              localStorage.setItem('aeroguard_tour_active', 'true');
              Object.keys(localStorage)
                .filter((k) => k.startsWith('aeroguard_tour_completed_'))
                .forEach((k) => localStorage.removeItem(k));
              localStorage.removeItem('aeroguard_tour_completed');
              window.location.reload();
            }}
            className="text-violet-300 hover:text-violet-200 hover:bg-violet-500/10 border border-violet-500/30 border-dashed"
            data-testid="dashboard-restart-tour-btn"
            title="Restart the guided product tour for your role"
          >
            <Play className="w-4 h-4 mr-2" />
            Take the tour again
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowCustomize(!showCustomize)}
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
            data-testid="customize-dashboard-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Customize
          </Button>
          {user?.role === 'admin' && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadAdminGuide}
              disabled={downloadingAdminGuide}
              className="border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/10 hover:text-cyan-100"
              data-testid="download-admin-guide-btn"
              title="Download the 11-page Administrator Setup Guide PDF"
            >
              <FileDown className="w-4 h-4 mr-2" />
              {downloadingAdminGuide ? 'Preparing…' : 'Admin Setup Guide'}
            </Button>
          )}
          {user?.role === 'admin' && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleSeedData}
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="seed-data-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Seed Demo Data
            </Button>
          )}
          <Button
            size="sm"
            onClick={() => onNavigate('incidents', { mode: 'create' })}
            className="bg-sky-600 hover:bg-sky-700"
            data-testid="report-incident-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('dashboard.reportIncident')}
          </Button>
        </div>
      </div>

      {/* Customization Panel */}
      {showCustomize && (
        <Card className="bg-slate-900/80 border-slate-700 animate-fade-in">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Settings className="w-5 h-5 text-sky-400" />
              Customize Dashboard Widgets
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-slate-400 text-sm mb-4">
              Toggle widgets on/off based on your preferences. Your role ({user?.role?.replace('_', ' ')}) has access to specific widgets.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {availableWidgets.map((widgetId) => {
                const config = WIDGET_CONFIG[widgetId];
                const Icon = config?.icon || BarChart3;
                const isVisible = widgetVisibility[widgetId];
                return (
                  <button
                    key={widgetId}
                    onClick={() => toggleWidget(widgetId)}
                    className={cn(
                      "flex items-center gap-2 p-3 rounded-lg border transition-all",
                      isVisible 
                        ? "bg-sky-600/20 border-sky-500/50 text-sky-300" 
                        : "bg-slate-800/50 border-slate-700 text-slate-500"
                    )}
                    data-testid={`toggle-widget-${widgetId}`}
                  >
                    {isVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    <Icon className="w-4 h-4" />
                    <span className="text-sm truncate">{config?.title || widgetId}</span>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* GACA Readiness — single executive summary card */}
      {readiness && (
        <Card className="bg-gradient-to-br from-sky-950/60 via-slate-900/80 to-slate-950 border-sky-500/30 backdrop-blur-sm animate-fade-in" data-testid="gaca-readiness-card">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-center">
              {/* Score + label */}
              <div className="lg:col-span-3 flex items-center gap-4">
                <div className="relative w-24 h-24 shrink-0">
                  <svg viewBox="0 0 64 64" className="w-full h-full -rotate-90">
                    <circle cx="32" cy="32" r="28" stroke="rgba(148,163,184,0.2)" strokeWidth="6" fill="none" />
                    <circle cx="32" cy="32" r="28" stroke="rgb(56,189,248)" strokeWidth="6" fill="none"
                            strokeDasharray={`${(readiness.readiness.pct / 100) * 175.93} 175.93`}
                            strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-2xl font-bold text-white tabular-nums" data-testid="readiness-pct">{readiness.readiness.pct}%</span>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-sky-300 font-semibold">
                    <Award className="w-3.5 h-3.5" /> GACA Readiness
                  </div>
                  <div className="text-lg font-semibold text-white mt-0.5" data-testid="readiness-label">{readiness.readiness.label}</div>
                  <div className="text-xs text-slate-400 mt-1">
                    {readiness.maturity.items_assessed}/{readiness.maturity.items_total} items assessed
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={openActionPlan}
                    className="mt-2 h-7 text-xs border-sky-500/40 text-sky-200 hover:bg-sky-500/10"
                    data-testid="readiness-action-plan-btn"
                  >
                    <Sparkles className="w-3 h-3 mr-1.5" /> View Action Plan
                  </Button>
                </div>
              </div>

              {/* Sub-metrics */}
              <div className="lg:col-span-9 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <ReadinessChip
                  label="Maturity"
                  value={`${readiness.maturity.pct}%`}
                  testid="readiness-maturity"
                  onClick={() => onNavigate('gaca-audit')}
                  tone="sky"
                />
                <ReadinessChip
                  label="AE Engagement"
                  value={`${readiness.ae_engagement.pct}%`}
                  sub={`${readiness.ae_engagement.ae_attended}/${readiness.ae_engagement.total} mtgs`}
                  testid="readiness-ae"
                  onClick={() => onNavigate('ae-engagement')}
                  tone="amber"
                />
                <ReadinessChip
                  label="Resource Gaps"
                  value={readiness.resource_gaps.open}
                  sub={readiness.resource_gaps.critical > 0 ? `${readiness.resource_gaps.critical} critical` : 'open'}
                  testid="readiness-gaps"
                  onClick={() => onNavigate('resource-adequacy')}
                  tone={readiness.resource_gaps.critical > 0 ? 'rose' : (readiness.resource_gaps.open > 0 ? 'orange' : 'emerald')}
                />
                <ReadinessChip
                  label="SMS Plan"
                  value={`${readiness.sms_plan.avg_pct}%`}
                  sub={`${readiness.sms_plan.completed}/${readiness.sms_plan.total} done`}
                  testid="readiness-plan"
                  onClick={() => onNavigate('sms-implementation-plan')}
                  tone="emerald"
                />
                <ReadinessChip
                  label="Training Effect."
                  value={`${readiness.training_effectiveness.avg_survey_score}%`}
                  sub={`${readiness.training_effectiveness.responses} resp.`}
                  testid="readiness-training"
                  onClick={() => onNavigate('training-compliance')}
                  tone="violet"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Readiness Action Plan dialog — turns the snapshot into a CAPA-style checklist */}
      <Dialog open={actionPlanOpen} onOpenChange={setActionPlanOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto bg-slate-950 border-slate-800 text-slate-100" data-testid="readiness-action-plan-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-sky-300" />
              GACA Readiness Action Plan
            </DialogTitle>
            <DialogDescription>
              Prioritised checklist generated from your live GACA data. Each action deep-links to the page where it can be resolved.
            </DialogDescription>
          </DialogHeader>

          {actionPlanLoading && (
            <div className="py-12 text-center text-slate-500" data-testid="action-plan-loading">Generating plan…</div>
          )}

          {!actionPlanLoading && actionPlanError && (
            <div className="py-8 text-center space-y-3" data-testid="action-plan-error">
              <AlertTriangle className="w-8 h-8 text-rose-400 mx-auto" />
              <div className="text-sm text-rose-300">{actionPlanError}</div>
              <Button onClick={openActionPlan} size="sm" className="bg-sky-600 hover:bg-sky-500">
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Retry
              </Button>
            </div>
          )}

          {!actionPlanLoading && !actionPlanError && actionPlan && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-2">
                  <div className="text-2xl font-bold text-rose-300 tabular-nums" data-testid="action-plan-high-count">{actionPlan.counts.high}</div>
                  <div className="uppercase tracking-wider text-rose-400/80">High</div>
                </div>
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-2">
                  <div className="text-2xl font-bold text-amber-300 tabular-nums" data-testid="action-plan-medium-count">{actionPlan.counts.medium}</div>
                  <div className="uppercase tracking-wider text-amber-400/80">Medium</div>
                </div>
                <div className="rounded-lg border border-slate-600/30 bg-slate-700/20 p-2">
                  <div className="text-2xl font-bold text-slate-300 tabular-nums" data-testid="action-plan-low-count">{actionPlan.counts.low}</div>
                  <div className="uppercase tracking-wider text-slate-400">Low</div>
                </div>
              </div>

              <div className="space-y-2">
                {actionPlan.actions.map((a) => {
                  const tone = a.priority === 'high'
                    ? 'border-rose-500/40 bg-rose-500/5'
                    : a.priority === 'medium'
                    ? 'border-amber-500/30 bg-amber-500/5'
                    : 'border-slate-700/60 bg-slate-900/40';
                  const badge = a.priority === 'high'
                    ? 'bg-rose-500/15 border-rose-500/40 text-rose-200'
                    : a.priority === 'medium'
                    ? 'bg-amber-500/15 border-amber-500/40 text-amber-200'
                    : 'bg-slate-700/40 border-slate-600/40 text-slate-300';
                  return (
                    <div key={a.id} className={`rounded-lg border ${tone} p-3`} data-testid={`action-plan-item-${a.id}`}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge className={`${badge} text-[10px] uppercase tracking-wider`}>{a.priority}</Badge>
                            <Badge variant="outline" className="text-[10px] border-slate-600 text-slate-400 uppercase tracking-wider">{a.category}</Badge>
                          </div>
                          <div className="text-sm font-semibold text-white">{a.title}</div>
                          <div className="text-xs text-slate-400 mt-1 leading-relaxed">{a.description}</div>
                          <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500">
                            <span><span className="opacity-70">Now:</span> <span className="text-slate-300">{a.evidence}</span></span>
                            <span>→</span>
                            <span><span className="opacity-70">Target:</span> <span className="text-emerald-300">{a.target}</span></span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 flex-wrap">
                          {a.linked_capa_id ? (
                            <Badge
                              className="bg-emerald-500/15 border-emerald-500/40 text-emerald-200 text-[11px]"
                              data-testid={`action-plan-linked-${a.id}`}
                            >
                              <CheckCircle2 className="w-3 h-3 mr-1" /> {a.linked_capa_number}
                            </Badge>
                          ) : a.capa_eligible ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => convertActionToCapa(a.id)}
                              className="border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10"
                              data-testid={`action-plan-convert-${a.id}`}
                              title="Create a tracked CAPA record from this action"
                            >
                              <ClipboardCheck className="w-3.5 h-3.5 mr-1.5" /> Convert to CAPA
                            </Button>
                          ) : null}
                          <Button
                            size="sm"
                            onClick={() => { setActionPlanOpen(false); onNavigate(a.deep_link); }}
                            className="bg-sky-600 hover:bg-sky-500"
                            data-testid={`action-plan-cta-${a.id}`}
                          >
                            {a.cta} <ChevronRight className="w-3.5 h-3.5 ml-1" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <DialogFooter>
            <div className="text-[11px] text-slate-500 mr-auto">
              {actionPlan?.generated_at ? `Generated ${formatDistanceToNow(new Date(actionPlan.generated_at), { addSuffix: true })}` : ''}
            </div>
            <Button variant="outline" onClick={() => setActionPlanOpen(false)} className="border-slate-700">Close</Button>
            <Button onClick={openActionPlan} className="bg-sky-600 hover:bg-sky-500" data-testid="action-plan-refresh">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats Cards - Always visible if enabled */}
      {isWidgetVisible('stats') && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-slate-900/60 border-slate-800 backdrop-blur-sm card-hover animate-fade-in" data-testid="stat-total-incidents">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm font-medium">{t('dashboard.totalIncidents')}</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats?.incidents?.total || 0}</p>
                  <p className="text-sm text-slate-500 mt-1">
                    {stats?.incidents?.open || 0} {t('common.open').toLowerCase()}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-sky-600/20 flex items-center justify-center">
                  <FileWarning className="w-6 h-6 text-sky-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/60 border-slate-800 backdrop-blur-sm card-hover animate-fade-in stagger-1" data-testid="stat-critical-risks">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm font-medium">{t('dashboard.criticalRisks')}</p>
                  <p className="text-3xl font-bold text-red-400 mt-1">{stats?.risks?.critical || 0}</p>
                  <p className="text-sm text-slate-500 mt-1">
                    {stats?.risks?.high || 0} {t('dashboard.highRisks').toLowerCase()}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-red-600/20 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/60 border-slate-800 backdrop-blur-sm card-hover animate-fade-in stagger-2" data-testid="stat-overdue-capas">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm font-medium">{t('dashboard.overdueCAPAs')}</p>
                  <p className={`text-3xl font-bold mt-1 ${(stats?.capas?.overdue || 0) > 0 ? 'text-orange-400' : 'text-emerald-400'}`}>
                    {stats?.capas?.overdue || 0}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    {stats?.capas?.open || 0} {t('common.open').toLowerCase()}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${(stats?.capas?.overdue || 0) > 0 ? 'bg-orange-600/20' : 'bg-emerald-600/20'}`}>
                  <ClipboardCheck className={`w-6 h-6 ${(stats?.capas?.overdue || 0) > 0 ? 'text-orange-400' : 'text-emerald-400'}`} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/60 border-slate-800 backdrop-blur-sm card-hover animate-fade-in stagger-3" data-testid="stat-planned-audits">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm font-medium">{t('dashboard.plannedAudits')}</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats?.audits?.planned || 0}</p>
                  <p className="text-sm text-slate-500 mt-1">
                    {stats?.audits?.total || 0} total
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-violet-600/20 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-violet-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Safety Bulletins Alert Widget */}
      {isWidgetVisible('bulletins') && recentBulletins.length > 0 && (
        <Card className="bg-slate-900/60 border-slate-800 backdrop-blur-sm border-l-4 border-l-red-500" data-testid="bulletins-widget">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-red-400" />
                Safety Alerts
              </CardTitle>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onNavigate('bulletins')}
                className="text-sky-400 hover:text-sky-300 hover:bg-sky-900/20"
                data-testid="view-all-bulletins-btn"
              >
                View All
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-3">
              {recentBulletins.map((bulletin) => {
                const isUrgent = bulletin.priority === 'urgent';
                const isHigh = bulletin.priority === 'high';
                return (
                  <div 
                    key={bulletin.id}
                    className={cn(
                      "flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors",
                      isUrgent ? "bg-red-950/30 hover:bg-red-950/50" : 
                      isHigh ? "bg-orange-950/30 hover:bg-orange-950/50" : 
                      "bg-slate-800/50 hover:bg-slate-800"
                    )}
                    onClick={() => onNavigate('bulletins', { bulletinId: bulletin.id })}
                  >
                    <div className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                      isUrgent ? "bg-red-600/30" : isHigh ? "bg-orange-600/30" : "bg-slate-700"
                    )}>
                      {isUrgent ? <AlertCircle className="w-4 h-4 text-red-400" /> :
                       isHigh ? <AlertTriangle className="w-4 h-4 text-orange-400" /> :
                       <Bell className="w-4 h-4 text-slate-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{bulletin.title}</p>
                      <p className="text-slate-400 text-sm truncate">{bulletin.content?.substring(0, 100)}...</p>
                      <p className="text-slate-500 text-xs mt-1">
                        {formatDistanceToNow(new Date(bulletin.created_at), { addSuffix: true })}
                      </p>
                    </div>
                    {isUrgent && (
                      <Badge className="bg-red-600/30 text-red-300 border-red-500/50">Urgent</Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Incident Trend Chart */}
        {isWidgetVisible('incidents_trend') && (
          <Card className="bg-slate-900/60 border-slate-800 backdrop-blur-sm" data-testid="incidents-trend-widget">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <Activity className="w-5 h-5 text-sky-400" />
                {t('dashboard.incidentTrend')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={incidentTrendData}>
                    <defs>
                      <linearGradient id="colorIncidents" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                    <YAxis stroke="#64748b" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                      labelStyle={{ color: '#f1f5f9' }}
                    />
                    <Area type="monotone" dataKey="count" stroke="#0ea5e9" fillOpacity={1} fill="url(#colorIncidents)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Risk Distribution Chart */}
        {isWidgetVisible('risks_overview') && (
          <Card className="bg-slate-900/60 border-slate-800 backdrop-blur-sm" data-testid="risks-overview-widget">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-400" />
                Risk Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-center justify-center">
                {severityData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={severityData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                        labelLine={false}
                      >
                        {severityData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-slate-500 text-center">
                    <AlertTriangle className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>No severity data available</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* AI Analysis Widget */}
      {isWidgetVisible('ai_analysis') && (
        <Card className="bg-slate-900/60 border-slate-800 backdrop-blur-sm" data-testid="ai-analysis-widget">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <Brain className="w-5 h-5 text-violet-400" />
                AI Safety Analysis
              </CardTitle>
              <Button
                size="sm"
                onClick={handleGetAIAnalysis}
                disabled={aiLoading}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="get-ai-analysis-btn"
              >
                {aiLoading ? (
                  <><span className="spinner w-4 h-4 mr-2" />Analyzing...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" />Get AI Insights</>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {aiAnalysis ? (
              <div className="space-y-4">
                <div className="p-4 bg-slate-800/50 rounded-lg">
                  <h4 className="text-white font-medium mb-2">Summary</h4>
                  <p className="text-slate-300 text-sm">{aiAnalysis.summary || 'No summary available'}</p>
                </div>
                {aiAnalysis.recommendations && aiAnalysis.recommendations.length > 0 && (
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <h4 className="text-white font-medium mb-2">Recommendations</h4>
                    <ul className="space-y-2">
                      {aiAnalysis.recommendations.map((rec, i) => (
                        <li key={i} className="text-slate-300 text-sm flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Brain className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Click &quot;Get AI Insights&quot; to analyze your safety data</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Role-specific widgets */}
      {isWidgetVisible('my_tasks') && (
        <Card className="bg-slate-900/60 border-slate-800 backdrop-blur-sm" data-testid="my-tasks-widget">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-emerald-400" />
              My Assigned Tasks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-6 text-slate-500">
              <Target className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">View your assigned CAPAs, investigations, and follow-ups</p>
              <Button 
                size="sm" 
                className="mt-3 bg-slate-700 hover:bg-slate-600"
                onClick={() => onNavigate('capas')}
              >
                View My Tasks
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isWidgetVisible('training') && (
        <Card className="bg-slate-900/60 border-slate-800 backdrop-blur-sm" data-testid="training-widget">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-white flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-sky-400" />
                Training Overview
              </CardTitle>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => onNavigate('training')}
                className="text-sky-400 hover:text-sky-300"
              >
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="p-4 bg-slate-800/50 rounded-lg">
                <p className="text-2xl font-bold text-sky-400">19</p>
                <p className="text-slate-400 text-sm">Programs</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-lg">
                <p className="text-2xl font-bold text-emerald-400">4</p>
                <p className="text-slate-400 text-sm">Upcoming</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-lg">
                <p className="text-2xl font-bold text-violet-400">6</p>
                <p className="text-slate-400 text-sm">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Compact, clickable sub-metric chip for the GACA Readiness widget
const ReadinessChip = ({ label, value, sub, testid, onClick, tone = 'sky' }) => {
  const tones = {
    sky: 'border-sky-500/30 hover:border-sky-400/60 text-sky-300',
    amber: 'border-amber-500/30 hover:border-amber-400/60 text-amber-300',
    emerald: 'border-emerald-500/30 hover:border-emerald-400/60 text-emerald-300',
    orange: 'border-orange-500/30 hover:border-orange-400/60 text-orange-300',
    rose: 'border-rose-500/40 hover:border-rose-400/60 text-rose-300',
    violet: 'border-violet-500/30 hover:border-violet-400/60 text-violet-300',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left px-3 py-2.5 rounded-lg bg-slate-900/40 border ${tones[tone] || tones.sky} transition-colors group`}
    >
      <div className="text-[10px] uppercase tracking-wider text-slate-500 group-hover:text-slate-400 mb-0.5">{label}</div>
      <div className="text-xl font-bold text-white tabular-nums leading-none">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-1">{sub}</div>}
    </button>
  );
};
